import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

export default function AdminCourseGrants({ users }) {
  const [userId, setUserId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [grants, setGrants] = useState([]);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selectedUser = users.find(u => u.user_id === userId);
  const selectedCourse = courses.find(c => c.contest_id === courseId);
  const message = err => typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Operazione non riuscita. Riprova.';
  useEffect(() => {
    api.get('/admin/contests').then(({ data }) => setCourses(data.items.filter(c => c.editorial_status === 'PUBLISHED' && (c.public_status === 'PUBLISHED' || c.public_status === undefined))))
      .catch(() => setError('Impossibile caricare i corsi. Ricarica la pagina.'));
  }, []);
  useEffect(() => {
    let current = true;
    setGrants([]);
    if (userId) api.get('/admin/course-grants', { params: { user_id: userId } })
      .then(({ data }) => { if (current) setGrants(data.items); })
      .catch(() => { if (current) setError('Impossibile caricare gli accessi. Ricarica la pagina.'); });
    return () => { current = false; };
  }, [userId]);
  const reload = async () => {
    const { data } = await api.get('/admin/course-grants', { params: { user_id: userId } });
    setGrants(data.items);
  };
  const grant = async event => {
    event.preventDefault();
    if (!window.confirm(`Attivare ${selectedCourse?.title} per ${selectedUser?.email}? Nessun addebito. Durata: ${duration === 'unlimited' ? 'senza scadenza' : duration + ' giorni'}.`)) return;
    setBusy(true);
    try {
      await api.post('/admin/course-grants', { user_id: userId, contest_id: courseId, reason: reason.trim(),
        expires_at: duration === 'unlimited' ? null : new Date(Date.now() + Number(duration) * 86400000).toISOString() });
      toast.success('Accesso incluso attivato. Nessun pagamento creato.');
      setReason('');
      await reload();
    } catch (err) { toast.error(message(err)); }
    finally { setBusy(false); }
  };
  const revoke = async item => {
    const explanation = window.prompt(`Motivo della revoca di ${item.contest_title} (almeno 5 caratteri). Gli acquisti restano validi.`);
    if (explanation === null) return;
    if (explanation.trim().length < 5) { toast.error('Indica un motivo di almeno 5 caratteri'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/course-grants/${item.grant_id}/revoke`, { reason: explanation.trim() });
      toast.success('Accesso incluso revocato');
      await reload();
    } catch (err) { toast.error(message(err)); }
    finally { setBusy(false); }
  };
  const field = 'mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm';
  return <section className="max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
    <h2 className="text-xl font-bold">Accessi inclusi nei libri</h2>
    <p className="mt-2 text-sm text-slate-600">Verifica la prova d’acquisto ricevuta via email, poi seleziona lo studente registrato e il corso concordato. Nessun ordine Stripe, ricavo o commissione affiliata viene creato.</p>
    {error && <p role="alert" className="mt-3 text-red-700">{error}</p>}
    <form onSubmit={grant} className="mt-5 grid gap-4">
      <label>Studente<select aria-label="Studente" required disabled={busy} className={field} value={userId} onChange={e => setUserId(e.target.value)}>
        <option value="">Seleziona nome / email</option>
        {users.filter(u => u.role === 'USER').map(u => <option key={u.user_id} value={u.user_id}>{u.email} — {u.name}</option>)}
      </select></label>
      <label>Corso<select aria-label="Corso" required disabled={busy} className={field} value={courseId} onChange={e => setCourseId(e.target.value)}>
        <option value="">Seleziona il corso incluso</option>
        {courses.map(c => <option key={c.contest_id} value={c.contest_id}>{c.title}</option>)}
      </select></label>
      <label>Durata<select aria-label="Durata" required disabled={busy} className={field} value={duration} onChange={e => setDuration(e.target.value)}>
        <option value="">Scegli le condizioni concordate</option><option value="30">30 giorni</option><option value="90">90 giorni</option><option value="180">180 giorni</option><option value="365">365 giorni</option><option value="unlimited">Senza scadenza</option>
      </select></label>
      <label>Motivo e riferimento interno<Input aria-label="Motivo e riferimento interno" required minLength={5} maxLength={500} disabled={busy} value={reason} onChange={e => setReason(e.target.value)} placeholder="Libro ASMEL, prova verificata, riferimento pratica" /></label>
      <p className="text-xs text-slate-500">Non inserire dati di pagamento o documenti personali. La motivazione resta riservata agli amministratori.</p>
      <Button type="submit" disabled={busy || !!error || !userId || !courseId || !duration || reason.trim().length < 5}>Attiva accesso incluso nel libro</Button>
    </form>
    {userId && <div className="mt-6 space-y-3"><h3 className="font-semibold">Accessi inclusi dello studente</h3>
      {!grants.length && <p className="text-sm text-slate-500">Nessun accesso incluso caricato.</p>}
      {grants.map(item => {
        const expired = item.expires_at && new Date(item.expires_at) <= new Date();
        return <div key={item.grant_id} className="rounded-lg border p-3 text-sm break-words">
          <p className="font-semibold">{item.contest_title}</p>
          <p>{item.revoked ? 'Revocato' : expired ? 'Scaduto' : 'Attivo'} · Scadenza: {item.expires_at ? new Date(item.expires_at).toLocaleString('it-IT') : 'nessuna'}</p>
          <p>{item.reason}</p>
          {!item.revoked && <Button type="button" variant="outline" className="mt-2" disabled={busy} onClick={() => revoke(item)}>Revoca accesso incluso</Button>}
          <details className="mt-2"><summary>Storico operazioni</summary>{item.history.map((entry, index) => <p key={index}>{new Date(entry.at).toLocaleString('it-IT')} · {entry.action} · {entry.by} · {entry.reason}</p>)}</details>
        </div>;
      })}
    </div>}
  </section>;
}
